import type { Plugin } from "unified";
import type { Parent } from "unist";

interface NodeWithPosition {
  type: string;
  position?: {
    start?: { line?: number };
    end?: { line?: number };
  };
  children?: NodeWithPosition[];
  data?: Record<string, unknown>;
}

const BLOCK_TYPES = new Set([
  "paragraph",
  "heading",
  "blockquote",
  "list",
  "listItem",
  "code",
  "table",
  "thematicBreak",
]);

const setEmptyLineData = (node: NodeWithPosition, gap: number) => {
  if (gap <= 0) return;

  node.data = node.data ?? {};
  node.data.emptyLinesBefore = gap;
  const hProperties: Record<string, unknown> =
    typeof node.data.hProperties === "object" && node.data.hProperties !== null
      ? (node.data.hProperties as Record<string, unknown>)
      : {};

  hProperties["data-empty-lines"] = String(gap);
  const existingStyle =
    typeof hProperties.style === "string" ? hProperties.style : "";
  const styleWithSeparator =
    existingStyle && !existingStyle.trim().endsWith(";")
      ? `${existingStyle};`
      : existingStyle;
  hProperties.style = `${styleWithSeparator}--md-empty-lines:${gap}`;
  node.data.hProperties = hProperties;
};

export const remarkPreserveEmptyLines: Plugin<[], Parent> = () => {
  return (tree) => {
    annotateSequentialBlocks(tree.children as NodeWithPosition[]);
  };
};

const annotateSequentialBlocks = (nodes: NodeWithPosition[] = []) => {
  if (!nodes.length) return;

  const firstLine = nodes[0]?.position?.start?.line;
  let lastEndLine = typeof firstLine === "number" ? firstLine - 1 : 0;
  let previousBlockType: string | null = null;

  for (const node of nodes) {
    if (BLOCK_TYPES.has(node.type)) {
      const startLine = node.position?.start?.line;
      if (typeof startLine === "number") {
        const gap = startLine - lastEndLine - 1;
        const visibleGap =
          previousBlockType === "blockquote" ? Math.max(0, gap - 1) : gap;
        setEmptyLineData(node, visibleGap);
      }

      const endLine = node.position?.end?.line;
      if (typeof endLine === "number") {
        lastEndLine = endLine;
      }
      previousBlockType = node.type;
    }

    if (node.children?.length) {
      annotateSequentialBlocks(node.children);
      const lastChild = node.children[node.children.length - 1];
      const childEnd = lastChild?.position?.end?.line;
      if (typeof childEnd === "number") {
        lastEndLine = Math.max(lastEndLine, childEnd);
      }
    }
  }
};
